import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { DangerousLoadCategory } from "@/lib/api/loads";
import {
  createDangerousCategory,
  updateDangerousCategory,
} from "@/lib/api/catalogs";
import { getDangerousCategories } from "@/lib/api/loads";
import {
  LocalizedNamesFields,
  toLocalizedName,
  type LocalizedNamesDraft,
} from "@/components/admin/catalogs/localized-names";
import { Text } from "@/components/ui/text";
import { CatalogPageTemplate } from "@/components/admin/catalog-page-template";

type DangerousCategoryRow = DangerousLoadCategory & {
  created_at?: string;
  is_deleted?: boolean;
};

function AdminDangerousCategoriesCatalogPage() {
  const { t } = useTranslation("admin.catalogs.dangerousCategories");

  return (
    <CatalogPageTemplate<DangerousCategoryRow>
      title={t("title")}
      tableId="catalog-dangerous-categories"
      queryKey={["admin", "catalogs", "dangerous-categories"]}
      fetchFn={getDangerousCategories}
      columns={[
        {
          key: "nameEn",
          header: t("columns.nameEn"),
          defaultWidth: 200,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              weight="medium"
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.en ?? item.name}
            </Text>
          ),
        },
        {
          key: "nameRu",
          header: t("columns.nameRu"),
          defaultWidth: 180,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.ru ?? "—"}
            </Text>
          ),
        },
        {
          key: "nameUz",
          header: t("columns.nameUz"),
          defaultWidth: 180,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.uz ?? "—"}
            </Text>
          ),
        },
      ]}
      searchFilter={(item, q) => {
        const names = [
          item.name,
          item.names?.en,
          item.names?.ru,
          item.names?.uz,
          item.names?.tr,
          item.names?.uz_cyr,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return names.includes(q);
      }}
      searchPlaceholder={t("searchPlaceholder")}
      emptyMessage={t("noItems")}
      addLabel={t("add")}
      editTitle={t("modalEditTitle")}
      addTitle={t("modalAddTitle")}
      failedToLoadMessage={t("failedToLoad")}
      failedToSaveMessage={t("failedToSave")}
      deleteEndpoint="/dangerous-categories"
      editLabel={t("edit")}
      itemToFormData={(item) => ({
        namesDraft: {
          en: item.names?.en ?? item.name ?? "",
          ru: item.names?.ru ?? "",
          uz: item.names?.uz ?? "",
        } as LocalizedNamesDraft,
      })}
      emptyFormData={() => ({
        namesDraft: { en: "", ru: "", uz: "" } as LocalizedNamesDraft,
      })}
      formDataToPayload={(fd) => ({
        name: (fd.namesDraft as LocalizedNamesDraft).en,
        names: toLocalizedName(fd.namesDraft as LocalizedNamesDraft),
      })}
      isFormValid={(fd) =>
        Boolean((fd.namesDraft as LocalizedNamesDraft).en.trim())
      }
      createFn={(payload) =>
        createDangerousCategory(
          payload as Parameters<typeof createDangerousCategory>[0],
        )
      }
      updateFn={(id, payload) =>
        updateDangerousCategory(
          id,
          payload as Parameters<typeof updateDangerousCategory>[1],
        )
      }
      renderForm={(formData, setFormData, submitting) => (
        <LocalizedNamesFields
          value={formData.namesDraft as LocalizedNamesDraft}
          onChange={(next) =>
            setFormData((prev) => ({ ...prev, namesDraft: next }))
          }
          disabled={submitting}
          testIdPrefix="admin-dangerous-category"
        />
      )}
      testIdPrefix="admin-catalog-dangerous-categories"
    />
  );
}

export const Route = createFileRoute(
  "/_app/admin/catalogs/dangerous-categories",
)({
  component: AdminDangerousCategoriesCatalogPage,
});
